import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  Switch, 
  FormControlLabel, 
  Alert, 
  CircularProgress, 
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import axios from 'axios';
import { useThemeContext } from '../contexts/ThemeContext';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SettingSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

/**
 * Settings Component
 * 
 * Allows users to configure application settings
 */
const Settings = () => {
  const { darkMode, setDarkMode } = useThemeContext();
  // Stable ref so the one-time mount effect can read the initial value
  const darkModeRef = React.useRef(darkMode);

  // State
  const [settings, setSettings] = useState({
    cleanupDays: 7,
    darkMode: false,
    notificationsEnabled: true,
    maxConcurrentJobs: 1
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  
  // Fetch settings on mount – read from localStorage once.
  // darkMode initial value comes from ThemeContext (already seeded from localStorage),
  // so we don't need darkMode as a dependency here.
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        const savedSettings = localStorage.getItem('themeGeneratorSettings');
        
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          // Prefer the live context value for darkMode so the toggle reflects reality
          setSettings({ ...parsed, darkMode: darkModeRef.current });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        setError('Failed to load settings. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep local toggle in sync when global darkMode changes externally
  useEffect(() => {
    setSettings(prev => ({ ...prev, darkMode }));
  }, [darkMode]);
  
  // Handle settings change – immediately apply dark mode globally
  const handleSettingsChange = (event) => {
    const { name, value, checked, type } = event.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setSettings(prevSettings => ({
      ...prevSettings,
      [name]: newValue
    }));

    // Apply dark mode change instantly so users get immediate visual feedback
    if (name === 'darkMode') {
      setDarkMode(checked);
    }
  };
  
  // Handle settings save
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // In a real implementation, this would be an API call
      // For now, we'll simulate saving settings to localStorage
      localStorage.setItem('themeGeneratorSettings', JSON.stringify(settings));
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaving(false);
      setSuccess('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError(`Failed to save settings: ${err.message}`);
      setSaving(false);
    }
  };
  
  // Handle cleanup dialog open
  const handleCleanupDialogOpen = () => {
    setCleanupDialogOpen(true);
  };
  
  // Handle cleanup dialog close
  const handleCleanupDialogClose = () => {
    setCleanupDialogOpen(false);
  };
  
  // Handle project cleanup
  const handleProjectCleanup = async () => {
    try {
      setCleanupLoading(true);
      setError(null);
      
      // API call to clean up old projects
      const response = await axios.post('/api/projects/cleanup', {
        maxAgeDays: settings.cleanupDays
      });
      
      setCleanupResult(response.data);
      setCleanupLoading(false);
    } catch (err) {
      console.error('Failed to clean up projects:', err);
      setError(`Failed to clean up projects: ${err.response?.data?.message || err.message}`);
      setCleanupLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      
      <Typography variant="subtitle1" color="textSecondary" paragraph>
        Configure application settings and manage your WordExPress Theme Generator
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <StyledPaper>
            <SettingSection>
              <Typography variant="h6" gutterBottom>
                Application Settings
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.darkMode}
                        onChange={handleSettingsChange}
                        name="darkMode"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {settings.darkMode
                          ? <DarkModeIcon fontSize="small" />
                          : <LightModeIcon fontSize="small" />}
                        Dark Mode
                      </Box>
                    }
                  />
                  <Typography variant="body2" color="textSecondary">
                    {settings.darkMode ? 'Dark theme is active' : 'Light theme is active'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notificationsEnabled}
                        onChange={handleSettingsChange}
                        name="notificationsEnabled"
                      />
                    }
                    label="Enable Notifications"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Receive notifications for theme generation events
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Concurrent Jobs"
                    name="maxConcurrentJobs"
                    value={settings.maxConcurrentJobs}
                    onChange={handleSettingsChange}
                    type="number"
                    inputProps={{ min: 1, max: 5 }}
                    helperText="Maximum number of concurrent theme generation jobs"
                  />
                </Grid>
              </Grid>
            </SettingSection>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Box>
          </StyledPaper>
          
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              Data Management
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" component="h3">
                        Project Cleanup
                      </Typography>
                      
                      <Tooltip title="Automatically remove old projects to free up storage space">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Configure automatic cleanup of old projects
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Cleanup Threshold (Days)"
                      name="cleanupDays"
                      value={settings.cleanupDays}
                      onChange={handleSettingsChange}
                      type="number"
                      inputProps={{ min: 1, max: 30 }}
                      helperText="Projects older than this many days will be removed"
                      sx={{ mb: 2 }}
                    />
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<CleaningServicesIcon />}
                      onClick={handleCleanupDialogOpen}
                    >
                      Run Cleanup Now
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h6" component="h3">
                        Clear All Data
                      </Typography>
                      
                      <Tooltip title="Remove all projects and reset application data">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Typography variant="body2" color="textSecondary" paragraph>
                      Remove all projects and reset application data
                    </Typography>
                    
                    <Typography variant="body2" color="error">
                      Warning: This action cannot be undone. All projects and settings will be permanently deleted.
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                    >
                      Clear All Data
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          </StyledPaper>
          
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              About
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Application Version:
                </Typography>
                <Typography variant="body1" paragraph>
                  WordExPress Theme Generator v1.0.0
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Claude API Version:
                </Typography>
                <Typography variant="body1" paragraph>
                  Claude 3 Opus
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Algorithm Version:
                </Typography>
                <Typography variant="body1" paragraph>
                  WordExPress Theme Generator System V7.1
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Support:
                </Typography>
                <Typography variant="body1" paragraph>
                  For support, please contact support@wordpress-theme-generator.com
                </Typography>
              </Grid>
            </Grid>
          </StyledPaper>
        </>
      )}
      
      {/* Cleanup Confirmation Dialog */}
      <Dialog
        open={cleanupDialogOpen}
        onClose={handleCleanupDialogClose}
      >
        <DialogTitle>Run Project Cleanup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all projects older than {settings.cleanupDays} days.
            This action cannot be undone. Do you want to continue?
          </DialogContentText>
          
          {cleanupResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Cleanup completed successfully! {cleanupResult.cleanedCount} projects were removed.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCleanupDialogClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleProjectCleanup} 
            color="primary" 
            variant="contained"
            disabled={cleanupLoading}
            startIcon={cleanupLoading && <CircularProgress size={20} color="inherit" />}
          >
            {cleanupLoading ? 'Cleaning...' : 'Run Cleanup'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;